<template>
  <div class="auth invitation-page">
    <NavBar v-if="me" />

    <p v-if="view === 'loading'" class="loading">{{ t('page-loading') }}</p>

    <div v-else-if="view === 'inviter'">
      <!-- Just made: says so; later: how long it has left (SPEC §4.14). -->
      <h3>{{ arrived ? t('invitation-ready-title') : t('invitation-waiting-title', { days: daysLeft }) }}</h3>
      <p class="expires">{{ t('invitation-expires', { date: expiryDate }) }}</p>
      <p class="settings">{{ settingsText }}</p>
      <component v-for="(section, i) in shareSections" :key="i" :is="section" :share="share" />
      <template v-if="!shareSections.length">
        <p><button type="button" class="main" @click="copyLink">{{ t('button-invitation-copy-link') }}</button></p>
        <p v-if="canShare"><button type="button" class="share" @click="shareLink">{{ t('button-invitation-share') }}</button></p>
        <p v-if="copied" class="info">{{ t('invitation-link-copied') }}</p>
      </template>
      <MessageBox v-model="confirmBox" />
      <p v-if="!confirmBox"><button type="button" class="withdraw" @click="confirmWithdraw">{{ t('button-invitation-withdraw') }}</button></p>
      <p v-if="message" class="warn">{{ message }}</p>
    </div>

    <div v-else-if="view === 'friend'">
      <img class="logo" src="@/assets/slowgo.png" :alt="t('app-name')" />
      <h3 class="title">{{ t('invitation-title', { name: inviterName }) }}</h3>
      <p class="settings">{{ settingsText }}</p>
      <component v-for="(section, i) in shareSections" :key="i" :is="section" :share="share" />
      <p><ActionButton :key="id" class="main accept" :disabled="busy" :action="accept" :label="t('button-accept')" /></p>
      <p v-if="message" class="warn">{{ message }}</p>
    </div>

    <div v-else-if="view === 'withdrawn'">
      <p class="info">{{ t('invitation-withdrawn') }}</p>
      <router-link :to="{ name: 'list' }">{{ t('nav-back-to-list') }}</router-link>
    </div>

    <p v-else-if="view === 'taken'" class="warn">{{ t('invitation-taken') }}</p>
    <p v-else-if="view === 'unavailable'" class="warn">{{ t('invitation-unavailable') }}</p>
    <p v-else class="warn">{{ message }}</p>
  </div>
</template>

<script>
// An invitation (SPEC §2.5, §4.14): readable without sign-in. Its inviter sends it on or withdraws
// it; anyone else accepts it, signing in first when needed and coming back with ?accept=1.
import NavBar from './NavBar';
import ActionButton from './ActionButton';
import MessageBox from './MessageBox';
import api from '@/api/api';
import extensions from '@/extensions';
import { t, errorText } from '@/texts';
import { copyText } from '@/utils';
import { handicapText } from '@/text';

// How often the inviter's page looks whether the invitation was accepted.
const ACCEPTANCE_CHECK_MS = 4000;

export default {
  name: 'InvitationPage',
  components: { ActionButton, NavBar, MessageBox },
  data() {
    return {
      inv: null,
      view: 'loading',
      message: null,
      busy: false,
      copied: false,
      confirmBox: null,
      shareSections: extensions.shareSections,
      canShare: typeof navigator.share === 'function',
      // Opened from the create page (?send=1): a module may offer sending it at once.
      arrived: !!this.$route.query.send,
    };
  },
  computed: {
    me() { return this.$store.state.user; },
    id() { return this.$route.params.id; },
    path() { return `/invitations/${this.id}`; },
    link() { return window.location.origin + this.path; },
    inviterName() {
      const p = this.inv && this.inv.game.players.find(x => x.id === this.inv.createdBy);
      return p ? p.displayName : '';
    },
    // Whole days until the invitation expires, at least 1 while it is open.
    daysLeft() {
      return Math.max(1, Math.ceil((new Date(this.inv.expiresAt).getTime() - Date.now()) / (24 * 3600 * 1000)));
    },
    expiryDate() {
      const d = new Date(this.inv.expiresAt);
      return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    // The colour from the reader's side: the inviter's own, or the friend's.
    settingsText() {
      const setup = this.inv.game.setup;
      let colour;
      if (setup.nigiri) colour = t('create-colour-nigiri');
      else {
        const inviterBlack = setup.blackPlayer === this.inv.createdBy;
        const readerBlack = this.view === 'inviter' ? inviterBlack : !inviterBlack;
        colour = t(readerBlack ? 'invitation-colour-black' : 'invitation-colour-white');
      }
      if (setup.handicap) return t('invitation-settings-handicap', { grid: setup.grid, handicap: handicapText(setup.handicap), colour });
      return t('invitation-settings', { grid: setup.grid, colour });
    },
    share() {
      return {
        kind: 'invitation',
        id: this.id,
        grid: this.inv.game.setup.grid,
        handicap: this.inv.game.setup.handicap || 0,
        path: this.path,
        inviter: this.inviterName,
        viewer: this.me ? this.me.displayName : '',
        offer: this.view === 'inviter',
        arrived: this.arrived,
      };
    },
  },
  methods: {
    t,
    async load() {
      this.message = null;
      try {
        this.inv = await api.call('GET', `invitations/${this.id}`);
      } catch (e) {
        if (e.msg === 'not-found') this.view = 'unavailable';
        else { this.message = t('load-failed', { reason: errorText(e.msg || String(e)) }); this.view = 'failed'; }
        return false;
      }
      if (this.inv.status === 'accepted') {
        if (this.inv.gameId) this.$router.replace({ name: 'game', params: { id: this.inv.gameId } });
        else this.view = 'taken';
        return false;
      }
      this.view = this.me && this.me.id === this.inv.createdBy ? 'inviter' : 'friend';
      return true;
    },
    signInThen(next) {
      const provider = extensions.signInProviders.find(p => p.available());
      if (provider) provider.signIn(next);
      else this.$router.push({ name: 'signin', query: { next } });
    },
    async accept() {
      if (!this.me) {
        this.signInThen(`${this.path}?accept=1`);
        return;
      }
      this.busy = true;
      this.message = null;
      try {
        const { gameId } = await api.call('POST', `invitations/${this.id}/acceptance`, {});
        this.$router.replace({ name: 'game', params: { id: gameId } });
      } catch (e) {
        const code = e.msg || String(e);
        if (code === 'already-done' || code === 'forbidden') await this.load();
        else if (code === 'not-found') this.view = 'unavailable';
        else this.message = errorText(code);
      } finally {
        this.busy = false;
      }
    },
    async copyLink() {
      this.copied = false;
      await copyText(this.link);
      this.copied = true;
    },
    async shareLink() {
      try {
        await navigator.share({ title: t('invitation-title', { name: this.inviterName }), url: this.link });
      } catch (e) {
        // Closed without sharing.
      }
    },
    // While the inviter's view is shown, looks every few seconds (and when the page is shown again,
    // e.g. back from a WeChat chat) whether the invitation was accepted, and then opens the game.
    // Silent: a failed look waits for the next one.
    watchAcceptance() {
      if (this.view !== 'inviter' || this.watchTimer) return;
      this.onVisible = () => { if (!document.hidden) this.checkAcceptance(); };
      document.addEventListener('visibilitychange', this.onVisible);
      this.watchTimer = setInterval(() => { if (!document.hidden) this.checkAcceptance(); }, ACCEPTANCE_CHECK_MS);
    },
    stopWatching() {
      clearInterval(this.watchTimer);
      this.watchTimer = null;
      if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
    },
    async checkAcceptance() {
      if (this.view !== 'inviter') { this.stopWatching(); return; }
      try {
        const inv = await api.call('GET', `invitations/${this.id}`);
        if (inv.status === 'accepted' && inv.gameId && this.view === 'inviter') {
          this.stopWatching();
          this.$router.replace({ name: 'game', params: { id: inv.gameId } });
        }
      } catch (e) {
        // Tried again at the next look.
      }
    },
    confirmWithdraw() {
      this.confirmBox = {
        text: t('invitation-withdraw-confirm'),
        okText: t('button-confirm'),
        cancelText: t('dialog-cancel'),
        ok: () => this.withdraw(),
        cancel: () => {},
      };
    },
    async withdraw() {
      try {
        await api.call('DELETE', `invitations/${this.id}`);
        this.view = 'withdrawn';
      } catch (e) {
        const code = e.msg || String(e);
        if (code === 'already-done' || code === 'not-found') await this.load();
        else this.message = errorText(code);
      }
    },
  },
  async created() {
    const accepting = this.$route.query.accept;
    if (accepting || this.arrived) this.$router.replace({ path: this.path });
    if (await this.load() && accepting && this.view === 'friend' && this.me) {
      this.view = 'loading';
      await this.accept();
      if (this.view === 'loading') this.view = 'friend';
    }
    this.watchAcceptance();
  },
  beforeDestroy() {
    this.stopWatching();
  },
};
</script>

<style src="./auth.css"></style>
<style scoped>
/* The nav bar spans the page; the content keeps a margin. */
.invitation-page > :not(nav) {
  padding: 0 10px;
}

.expires {
  margin: 0 0 14px;
  color: #999;
  font-size: 14px;
}

.loading {
  margin-top: 40px;
}

.logo {
  width: 120px;
  height: 120px;
  margin-top: 16px;
}

.title {
  overflow-wrap: anywhere;
}

.settings {
  font-size: 18px;
}

.auth .main, .auth .share, .auth button.withdraw {
  width: 100%;
  max-width: 260px;
  height: 48px;
  font-size: 18px;
}

.auth button.withdraw {
  font-size: 17px;
}
</style>
